import { useQuery } from "@tanstack/react-query";
import { generateHTML, type JSONContent } from "@tiptap/core";
import { Color } from "@tiptap/extension-color";
import Highlight from "@tiptap/extension-highlight";
import Image from "@tiptap/extension-image";
import Link from "@tiptap/extension-link";
import { TextStyle } from "@tiptap/extension-text-style";
import StarterKit from "@tiptap/starter-kit";
import { useMemo, useRef } from "react";

import * as mineApi from "../../api";
import { mineQueryKeys } from "../../query-options";
import { hydrateAttachmentUrls, parseAttachmentUri } from "../../text-content";

const TEXT_CARD_PREVIEW_EXTENSIONS = [
  StarterKit.configure({
    heading: { levels: [1, 2, 3] },
    link: false,
  }),
  Link.configure({ openOnClick: false }),
  Image.configure({ allowBase64: false }),
  TextStyle,
  Color,
  Highlight.configure({ multicolor: true }),
];

const INLINE_CONTENT_CONTAINERS = new Set([
  "codeBlock",
  "heading",
  "paragraph",
  "tableCell",
  "tableHeader",
]);

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === "object" && value !== null;
}

function extractTextFromJsonNode(value: unknown): string {
  if (!isRecord(value)) {
    return "";
  }

  if (value.type === "text") {
    return typeof value.text === "string" ? value.text : "";
  }

  if (value.type === "hardBreak") {
    return "\n";
  }

  if (!Array.isArray(value.content)) {
    return "";
  }

  const separator =
    typeof value.type === "string" && INLINE_CONTENT_CONTAINERS.has(value.type)
      ? ""
      : value.type === "tableRow"
        ? "\t"
        : "\n";

  return value.content.map(extractTextFromJsonNode).filter(Boolean).join(separator);
}

function looksLikeSerializedJson(value: string) {
  return (
    (value.startsWith("{") || value.startsWith("[")) &&
    /"(?:attrs|content|marks|type)"\s*:/.test(value)
  );
}

type TextCardPreviewContent =
  | { type: "html"; value: string }
  | { type: "text"; value: string };

type ParsedTextCardPreview =
  | { type: "json"; content: JSONContent; attachmentIds: string[] }
  | TextCardPreviewContent;

function collectAttachmentIds(content: JSONContent) {
  const attachmentIds = new Set<string>();

  const visitNode = (node: JSONContent) => {
    if (node.type === "image") {
      const attachmentId = parseAttachmentUri(node.attrs?.src);

      if (attachmentId) {
        attachmentIds.add(attachmentId);
      }
    }

    node.content?.forEach(visitNode);
  };

  visitNode(content);
  return [...attachmentIds];
}

function removeUnresolvedAttachmentImages(content: JSONContent): JSONContent {
  return {
    ...content,
    content: content.content
      ?.filter(
        (node) =>
          node.type !== "image" || !parseAttachmentUri(node.attrs?.src),
      )
      .map(removeUnresolvedAttachmentImages),
  };
}

function parseTextCardPreview(note?: string): ParsedTextCardPreview {
  const rawNote = note?.trim() ?? "";

  if (!rawNote) {
    return { type: "text", value: "" };
  }

  try {
    const content = JSON.parse(rawNote) as unknown;

    if (typeof content === "string") {
      return { type: "text", value: content.trim() };
    }

    if (isRecord(content) && content.type === "doc") {
      const jsonContent = content as JSONContent;

      return {
        type: "json",
        content: jsonContent,
        attachmentIds: collectAttachmentIds(jsonContent),
      };
    }

    const plainText = extractTextFromJsonNode(content)
      .replace(/[ \t]+\n/g, "\n")
      .replace(/\n{3,}/g, "\n\n")
      .trim();

    return { type: "text", value: plainText };
  } catch {
    return {
      type: "text",
      value: looksLikeSerializedJson(rawNote) ? "" : rawNote,
    };
  }
}

export function TextCardPreview({
  itemId,
  note,
}: {
  itemId: string;
  note?: string;
}) {
  const parsedPreview = useMemo(() => parseTextCardPreview(note), [note]);
  const attachmentIds = parsedPreview.type === "json"
    ? parsedPreview.attachmentIds
    : [];
  const retriedImageUrlsRef = useRef(new Set<string>());
  const attachmentsQuery = useQuery({
    enabled: attachmentIds.length > 0,
    queryKey: mineQueryKeys.itemAttachments(itemId),
    queryFn: () => mineApi.getItemAttachments(itemId),
    staleTime: 30_000,
  });
  const preview = useMemo<TextCardPreviewContent>(() => {
    if (parsedPreview.type !== "json") {
      return parsedPreview;
    }

    let content = parsedPreview.content;

    if (parsedPreview.attachmentIds.length > 0) {
      const attachmentUrlMap = new Map(
        attachmentsQuery.data?.attachments.map((attachment) => [
          attachment.id,
          attachment.download_url,
        ]) ?? [],
      );

      content = removeUnresolvedAttachmentImages(
        hydrateAttachmentUrls(content, attachmentUrlMap),
      );
    }

    try {
      return {
        type: "html",
        value: generateHTML(content, TEXT_CARD_PREVIEW_EXTENSIONS),
      };
    } catch {
      return {
        type: "text",
        value: extractTextFromJsonNode(content).trim(),
      };
    }
  }, [attachmentsQuery.data, parsedPreview]);

  if (preview.type === "text") {
    return (
      <p className="whitespace-pre-line text-[14px] leading-7 text-muted-foreground/90">
        {preview.value || "No content available."}
      </p>
    );
  }

  return (
    <div
      className="pointer-events-none text-[14px] leading-6 text-muted-foreground/90 [&_a]:text-primary [&_a]:underline [&_a]:underline-offset-2 [&_blockquote]:my-2 [&_blockquote]:border-l-[3px] [&_blockquote]:border-muted-foreground/30 [&_blockquote]:pl-3 [&_blockquote]:text-muted-foreground/75 [&_code]:rounded [&_code]:bg-muted [&_code]:px-1 [&_code]:py-0.5 [&_code]:font-mono [&_code]:text-[12px] [&_h1]:mb-2 [&_h1]:text-[16px] [&_h1]:font-bold [&_h1]:leading-6 [&_h1]:text-foreground [&_h2]:mb-2 [&_h2]:text-[15px] [&_h2]:font-bold [&_h2]:leading-6 [&_h2]:text-foreground [&_h3]:mb-2 [&_h3]:font-semibold [&_h3]:text-foreground/90 [&_img]:my-2 [&_img]:h-24 [&_img]:w-full [&_img]:rounded-lg [&_img]:object-cover [&_li]:my-1 [&_ol]:my-2 [&_ol]:list-decimal [&_ol]:pl-5 [&_p]:mb-2 [&_pre]:my-2 [&_pre]:overflow-x-auto [&_pre]:rounded-md [&_pre]:bg-muted [&_pre]:p-3 [&_pre]:font-mono [&_pre]:text-xs [&_pre_code]:rounded-none [&_pre_code]:bg-transparent [&_pre_code]:p-0 [&_pre_code]:text-inherit [&_strong]:font-semibold [&_strong]:text-foreground/90 [&_ul]:my-2 [&_ul]:list-disc [&_ul]:pl-5"
      dangerouslySetInnerHTML={{ __html: preview.value }}
      onErrorCapture={(event) => {
        if (!(event.target instanceof HTMLImageElement)) {
          return;
        }

        const failedUrl = event.target.currentSrc || event.target.src;

        if (!failedUrl || retriedImageUrlsRef.current.has(failedUrl)) {
          return;
        }

        retriedImageUrlsRef.current.add(failedUrl);
        void attachmentsQuery.refetch();
      }}
    />
  );
}
